import { authStore } from "@kaplan-labs/up-auth-api-client";

type HeapState = "idle" | "loading" | "active" | "suspended" | "failed";

let state: HeapState = "idle";
let heapId: string | undefined;
let authUnsubscribe: (() => void) | undefined;
// Whether the heap-<id>.js bundle has finished executing.
let scriptSettled = false;
// Set when consent is revoked while the bundle is still in flight, so the
// suspension is applied once the bundle finishes executing.
let pendingSuspend = false;

function injectScript(id: string): void {
  // Heap's standard loader pattern: an array that doubles as a queue.
  // Method calls are pushed as sub-arrays until the real SDK replaces this stub.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const heap = [] as any;
  heap.appid = id;
  heap.config = {};

  const methods = [
    "addEventProperties",
    "addUserProperties",
    "clearEventProperties",
    "identify",
    "resetIdentity",
    "removeEventProperty",
    "setEventProperties",
    "track",
    "unsetEventProperty",
  ];

  for (const method of methods) {
    heap[method] = (...args: unknown[]) => {
      heap.push([method, ...args]);
    };
  }

  window.heap = heap;

  const script = document.createElement("script");
  script.id = "heap-script";
  script.type = "text/javascript";
  script.async = true;
  script.src = `https://cdn.heapanalytics.com/js/heap-${id}.js`;
  script.addEventListener("load", handleScriptLoad);
  script.addEventListener("error", handleScriptError);
  document.head.appendChild(script);

  heap.load?.(id);
}

// Runs once the SDK bundle has finished executing. At this point window.heap is
// the real SDK, so any suspension deferred while the bundle was in flight can
// now be applied.
function handleScriptLoad(): void {
  scriptSettled = true;

  if (pendingSuspend) {
    pendingSuspend = false;
    suspend();
    return;
  }

  if (state === "loading") {
    state = "active";
  }
}

// The heap-<id>.js bundle failed to download or execute (network error, 404, or
// an adblocker blocking cdn.heapanalytics.com). The real SDK will never replace
// the stub, so there is nothing to activate or suspend. Tear down the auth
// listener and move to a terminal state. Later consent changes intentionally do
// NOT retry a script that just failed.
function handleScriptError(): void {
  scriptSettled = true;
  pendingSuspend = false;
  stopAuthListener();
  state = "failed";
}

function startAuthListener(): void {
  const identifyCurrentUser = () => {
    const $auth = authStore.get();
    if (window.heap && $auth.currentUser) {
      window.heap.identify($auth.currentUser.contactId as string);
    }
  };

  authUnsubscribe = authStore.listen(() => {
    identifyCurrentUser();
  });

  identifyCurrentUser();
}

function stopAuthListener(): void {
  if (authUnsubscribe) {
    authUnsubscribe();
    authUnsubscribe = undefined;
  }
}

// Heap gates its network/beacon sender on `window.heap.loaded`, so toggling the
// flag disables (false) or resumes (true) tracking without unloading the SDK.
function setHeapLoaded(loaded: boolean): void {
  if (window.heap) {
    (window.heap as { loaded?: boolean }).loaded = loaded;
  }
}

// Enable Heap for the current visitor. Call once consent for the analytics
// category is granted; safe to call repeatedly. Consent detection lives in the
// caller (see `useOneTrustConsent`), keeping this module consent-agnostic.
export function heapEnable(): void {
  if (!heapId) return;

  // Consent was re-granted after a suspension. The SDK is already present, so
  // re-enable it in place rather than injecting a second copy (which would
  // register a duplicate set of Heap listeners).
  if (state === "suspended") {
    pendingSuspend = false;
    setHeapLoaded(true);
    state = scriptSettled ? "active" : "loading";
    startAuthListener();
    return;
  }

  if (state !== "idle") return;

  state = "loading";
  injectScript(heapId);
  startAuthListener();
}

function suspend(): void {
  // Heap registers persistent listeners and timers (beforeunload, message,
  // scroll/mouse/touch handlers, a polling loop, cookie reads) that all read
  // `window.heap.loaded`. Removing the <script> element does not detach them,
  // and deleting window.heap makes them throw
  // "Cannot read properties of undefined (reading 'loaded')" the next time they
  // fire. So instead of unloading, we neutralize in place: flip the `loaded`
  // kill-switch to stop Heap's request pathway, then drop the current identity
  // and event properties.
  setHeapLoaded(false);

  if (window.heap) {
    window.heap.resetIdentity();
    window.heap.clearEventProperties();
  }

  stopAuthListener();
  state = "suspended";
}

// Disable Heap for the current visitor. Call when consent for the analytics
// category is denied or revoked; safe to call repeatedly and when Heap was
// never enabled.
export function heapDisable(): void {
  if (state !== "loading" && state !== "active") return;

  // The SDK bundle is still downloading/executing. Neutralizing now would be
  // undone when the bundle overwrites window.heap, so defer the suspension
  // until the script settles. window.heap is left in place so the in-flight
  // bundle can read window.heap.loaded without throwing.
  if (!scriptSettled) {
    pendingSuspend = true;
    stopAuthListener();
    state = "suspended";
    return;
  }

  suspend();
}

// Register the Heap app id and reset loader state. Does not read consent or
// load the SDK; callers gate `heapEnable`/`heapDisable` on consent (see
// `useOneTrustConsent`).
export function heapLoaderInit(id?: string): void {
  if (!id) return;

  // Idempotent for the same id: a remount (or React Strict Mode's double-invoke
  // in dev) re-runs the mount effect, but the loader is a module singleton, so
  // resetting state here would discard an in-flight/loaded SDK and let a
  // subsequent heapEnable() inject a duplicate <script>. Only (re)initialize
  // when the id actually changes.
  if (id === heapId) return;

  heapId = id;
  state = "idle";
  scriptSettled = false;
  pendingSuspend = false;
}

// Send only while Heap is enabled (consent granted): `loading` routes through
// Heap's own stub queue, `active` hits the real SDK. In every other state
// (`idle` before consent, `suspended` after revocation, `failed`) the call is
// dropped, not buffered — buffering and replaying across a later consent grant
// would transmit events recorded without consent, defeating the gate.
export function heapTrack(
  event: string,
  payload?: Record<string, unknown>,
): void {
  if (!heapId) return;

  if ((state === "active" || state === "loading") && window.heap) {
    window.heap.track(event, payload);
  }
}

export function heapIdentify(contactId: string): void {
  if (!heapId) return;

  if ((state === "active" || state === "loading") && window.heap) {
    window.heap.identify(contactId);
  }
}
